import { deleteField, doc, getDoc, serverTimestamp, Timestamp, updateDoc } from 'firebase/firestore'
import { auth, db } from '../utils/firebase'
import type { User, UserPreferences } from '../types/user'
import { defaultUserPreferences } from '../types/user'
import { AppError } from '../utils/errors'
import { attachmentStorageService } from './attachmentStorageService'
import type { NormalizedImage } from './imageProcessingService'

const mergePreferences = (preferences?: Partial<UserPreferences>): UserPreferences => ({
  ...defaultUserPreferences,
  ...preferences,
  notifications: {
    reminders: preferences?.notifications?.reminders ?? defaultUserPreferences.notifications.reminders,
    general: preferences?.notifications?.general ?? defaultUserPreferences.notifications.general,
  },
  privacy: { ...defaultUserPreferences.privacy, ...preferences?.privacy },
  location: { ...defaultUserPreferences.location, ...preferences?.location },
  automation: {
    ...defaultUserPreferences.automation,
    ...preferences?.automation,
    proactivity: {
      ...defaultUserPreferences.automation.proactivity,
      ...preferences?.automation?.proactivity,
      heartbeatNotificationsEnabled:
        preferences?.automation?.proactivity?.heartbeatNotificationsEnabled
        ?? preferences?.notifications?.reminders
        ?? defaultUserPreferences.automation.proactivity.heartbeatNotificationsEnabled,
    },
  },
  themeColors: {
    ...defaultUserPreferences.themeColors,
    ...preferences?.themeColors,
    logo: { ...defaultUserPreferences.themeColors.logo, ...preferences?.themeColors?.logo },
  },
})

export const userService = {
  async getCurrentUser(): Promise<User | null> {
    const userId = auth.currentUser?.uid
    if (!userId) return null
    const snapshot = await getDoc(doc(db, 'users', userId))
    if (!snapshot.exists()) return null
    const data = snapshot.data()
    const timestamp = Timestamp.now()
    return {
      ...data,
      id: userId,
      name: data.name || data.displayName || 'User',
      displayName: data.displayName || data.name || 'User',
      email: data.email || auth.currentUser?.email || '',
      emailVerified: data.emailVerified ?? auth.currentUser?.emailVerified ?? false,
      accountStatus: data.accountStatus || 'active',
      preferences: mergePreferences(data.preferences),
      createdAt: data.createdAt || timestamp,
      updatedAt: data.updatedAt || timestamp,
      lastLogin: data.lastLogin || timestamp,
    } as User
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<void> {
    if (!userId) throw new AppError('User ID is required', 'USER_ID_REQUIRED')
    const persistedUpdates = Object.fromEntries(
      Object.entries(updates).map(([key, value]) => [key, value === undefined ? deleteField() : value]),
    )
    await updateDoc(doc(db, 'users', userId), { ...persistedUpdates, updatedAt: serverTimestamp() })
  },


  async updateProfileImage(
    userId: string,
    image: NormalizedImage,
    previousStoragePath?: string,
  ): Promise<{ photoURL: string; photoStoragePath: string; photoByteSize: number }> {
    if (!userId) throw new AppError('User ID is required', 'USER_ID_REQUIRED')
    const uploaded = await attachmentStorageService.uploadProfileImage(userId, image)

    try {
      await updateDoc(doc(db, 'users', userId), {
        photoURL: uploaded.downloadUrl,
        photoStoragePath: uploaded.storagePath,
        photoByteSize: uploaded.byteSize,
        updatedAt: serverTimestamp(),
      })
    } catch (error) {
      await attachmentStorageService.deleteOwnedImage(userId, uploaded.storagePath).catch(() => undefined)
      throw error
    }

    if (previousStoragePath && previousStoragePath !== uploaded.storagePath) {
      await attachmentStorageService.deleteOwnedImage(userId, previousStoragePath).catch(() => undefined)
    }

    return {
      photoURL: uploaded.downloadUrl,
      photoStoragePath: uploaded.storagePath,
      photoByteSize: uploaded.byteSize,
    }
  },
  async updateUserPreferences(userId: string, preferences: UserPreferences): Promise<UserPreferences> {
    const validated = mergePreferences(preferences)
    await updateDoc(doc(db, 'users', userId), { preferences: validated, updatedAt: serverTimestamp() })
    return validated
  },
}
